import { useState } from "react";
import dynamic from 'next/dynamic';
import Router from 'next/router';
import Captcha from 'components/Captcha/Captcha';
import ErrorMessage from 'components/ErrorMessage';
import ServiceScheduleSchema from 'components/Forms/Schema/ServiceScheduleSchema';
import { isValidSchema } from 'utils/SchemaUtils';
import { phoneNumberWithDash } from "utils/commonUtils";
import { DropdownField, InputField, TextAreaField } from "./FormField";
import HtmlComponent from "components/Html/Html";

const Loading = dynamic(() => import('components/Loading'));

const initialState = {
    lotName: '',
    name: '',
    lastName: '',
    contactPreference: 'Phone',
    phone: '',
    email: '',
    year: '',
    make: '',
    model: '',
    mileage: '',
    underWarranty: 'No',
    requestedServices: [],
    description: '',
    preferredDate: '',
    time: '',
    captchaVerified: false,
    disclaimerAccepted: false,
};

const ServiceScheduleForm = (props: any) => {
    const lotSetup = props?.additionalValues?.lotSetup || [];
    const generalInfo = props?.settings?.generalInfo || {};
    const disclaimerText = props?.additionalValues?.disclaimerText || '';
    const servicesOfferedList = props?.additionalValues?.servicesOfferedList || [];

    const [record, setRecord] = useState(initialState);
    const [submitError, setSubmitError] = useState('');
    const [isSaving, setSaving] = useState(false);

    const onChange = ({ target: { name, value } }: React.ChangeEvent<HTMLInputElement | HTMLSelectElement | HTMLTextAreaElement>) => {
        if(name === 'phone')
            value = phoneNumberWithDash(value);

        const newRecord: any = {
            [name]: value,
        };

        if(name === 'lotName') {
            newRecord.position = '';
        }

        setRecord((item) => ({
            ...item,
            ...newRecord,
        }));
    };

    const toggleRequestedService = (e: React.ChangeEvent<HTMLInputElement>) => {
        const { value, checked } = e.target;

        setRecord((prev: any) => {
            const list: string[] = Array.isArray(prev.requestedServices) ? prev.requestedServices : [];
            if (checked) {
                return { ...prev, requestedServices: [...list, value] };
            } else {
                return { ...prev, requestedServices: list.filter((s) => s !== value) };
            }
        });
    };

    const toggleDisclaimerAccepted = (
        e: React.ChangeEvent<
    HTMLInputElement | HTMLSelectElement | HTMLTextAreaElement
  >
    ) => {
        const target = e.target as HTMLInputElement;

        setRecord((prev: any) => {
            return { ...prev, disclaimerAccepted: target.checked };
        });
    };

    const { isValid } = isValidSchema(ServiceScheduleSchema, record);

    const onSubmit = async () => {

        if (!isValid) return;
        if (!record.captchaVerified) return;
        setSaving(true);
        setSubmitError('');

        try {
            const contactName = `${record.name} ${record.lastName || ''}`.trim();
            const email = record.email ?? null;
            const toEmail = lotSetup?.find((c: any)=> c.lotName === record.lotName)?.emails?.replace(' ', '') || generalInfo.serviceEmail || generalInfo.hrEmail;

            const formData = new FormData();
            formData.append('from', contactName ? contactName : email);
            formData.append('to', toEmail);
            formData.append('subject', `Service Schedule Request : ${email ? email : contactName}`);
            formData.append('isHtml', 'true');

            const servicesList = Array.isArray(record.requestedServices) ? record.requestedServices.join(', ') : (record.requestedServices || '');

            const messageHtml = `
                <h3>Contact Info</h3>
                <strong>First Name: </strong><span>${record.name || ''}</span><br/>
                <strong>Last Name: </strong><span>${record.lastName || ''}</span><br/>
                <strong>Contact Preference: </strong><span>${record.contactPreference || ''}</span><br/>
                <strong>Cell phone: </strong><span>${record.phone || ''}</span><br/>
                <strong>Email Address: </strong><span>${record.email || ''}</span><br/>

                <h3>Vehicle Info</h3>
                <strong>Year: </strong><span>${record.year || ''}</span><br/>
                <strong>Make: </strong><span>${record.make || ''}</span><br/>
                <strong>Model: </strong><span>${record.model || ''}</span><br/>
                <strong>Under Warranty?: </strong><span>${record.underWarranty || ''}</span><br/>
                <strong>Mileage: </strong><span>${record.mileage || ''}</span><br/>

                <h3>Requested Service(s)</h3>
                <strong>Service(s) Needed: </strong><span>${servicesList || ''}</span><br/>
                <strong>Further Description: </strong><span>${record.description || ''}</span><br/>
                <strong>Preferred Date: </strong><span>${record.preferredDate || ''}</span><br/>
                <strong>Time: </strong><span>${record.time || ''}</span><br/>
                <strong>Lot: </strong><span>${record.lotName || ''}</span>
            `;

            formData.append('message', messageHtml);

            const response = await fetch('/api/mailWithAttachment', {
                method: 'POST',
                body: formData,
            });

            const result = await response.json();
            setSaving(false);

            if (result?.data) {
                Router.push("/service-schedule/thankyou");
            } else {
                setSubmitError('error while send email');
            }
        } catch (error: any) {
            setSaving(false);
            setSubmitError(error?.message || error);
        }
    };

    const onValidateCaptcha = async (val: boolean) => {
        setRecord({
            ...record,
            captchaVerified: val,
        });
    };

    return (
        <div className="container" id="servicescheduleform">
            <div className="row">
                <div style={{ marginTop: "10px" }} className="col-lg-12 col-md-12">
                    <div className="feature-box-5">
                        <h5>Contact Info</h5>
                        <div className="col-lg-6 col-md-6">
                            <div className="form-group row">
                                <label htmlFor="first-name" className="col-md-5">First Name *</label>
                                <div className="col-md-7">
                                    <InputField
                                        type="text"
                                        className="form-control"
                                        id="first-name"
                                        name="name"
                                        onChange={onChange}
                                        record={record}
                                        formSchema={ServiceScheduleSchema}
                                    />
                                </div>
                            </div>
                            <div className="form-group row">
                                <label htmlFor="last-name" className="col-md-5">Last Name *</label>
                                <div className="col-md-7">
                                    <InputField
                                        type="text"
                                        className="form-control"
                                        id="last-name"
                                        name="lastName"
                                        onChange={onChange}
                                        record={record}
                                        formSchema={ServiceScheduleSchema}
                                    />
                                </div>
                            </div>
                        </div>
                        <div className="col-lg-6 col-md-6">
                            <div className="form-group row">
                                <label htmlFor="cellphone" className="col-md-5">Cell Phone *</label>
                                <div className="col-md-7">
                                    <InputField
                                        type="tel"
                                        className="form-control isphone"
                                        id="cellphone"
                                        name="phone"
                                        onChange={onChange}
                                        record={record}
                                        value={record.phone}
                                        formSchema={ServiceScheduleSchema}
                                    />
                                </div>
                            </div>
                            <div className="form-group row">
                                <label htmlFor="emailaddress" className="col-md-5">Email Address *</label>
                                <div className="col-md-7">
                                    <InputField
                                        type="email"
                                        className="form-control"
                                        id="emailaddress"
                                        name="email"
                                        onChange={onChange}
                                        record={record}
                                        formSchema={ServiceScheduleSchema}
                                    />
                                </div>
                            </div>
                        </div>
                        <div className="col-lg-6 col-md-6"> 

                            <div className="form-group row align-items-center">
                                <label className="control-label col-md-5">Contact Preference *</label>
                                <div className="col-md-7">
                                    <div
                                        style={{
                                            display: "flex",
                                            alignItems: "center",
                                            gap: "24px",
                                            flexWrap: "nowrap",
                                            marginTop: "8px",
                                        }}
                                    >
                                        <label
                                            htmlFor="contact-phoneid"
                                            style={{
                                                display: "flex",
                                                alignItems: "center",
                                                gap: "6px",
                                                margin: 0,
                                                whiteSpace: "nowrap",
                                            }}
                                        >
                                            <InputField
                                                type="radio"
                                                id="contact-phoneid"
                                                name="contactPreference"
                                                value="Phone"
                                                onChange={onChange}
                                                record={record}
                                                formSchema={ServiceScheduleSchema}
                                                style={{ margin: 0, position: "relative", top: "-1px" }}
                                                className={""}                                            />
        Phone
                                        </label>

                                        <label
                                            htmlFor="contact-emailid"
                                            style={{
                                                display: "flex",
                                                alignItems: "center",
                                                gap: "6px",
                                                margin: 0,
                                                whiteSpace: "nowrap",
                                            }}
                                        >
                                            <InputField
                                                type="radio"
                                                id="contact-emailid"
                                                name="contactPreference"
                                                value="Email"
                                                onChange={onChange}
                                                record={record}
                                                formSchema={ServiceScheduleSchema}
                                                style={{ margin: 0, position: "relative", top: "-1px" }}
                                                className={""}                                            />
        Email
                                        </label>
                                    </div>
                                </div>
                            </div>
                        </div>
                    </div>
                </div>
            </div>

            <div className="row">
                <div style={{ marginTop: "10px" }} className="col-lg-12 col-md-12">
                    <div className="feature-box-5">
                        <h5>Vehicle Info</h5>
                        <div className="col-lg-6 col-md-6">
                            <div className="form-group row">
                                <label htmlFor="year" className="col-md-5">Year *</label>
                                <div className="col-md-7">
                                    <InputField
                                        type="text"
                                        className="form-control"
                                        id="year"
                                        name="year"
                                        onChange={onChange}
                                        record={record}
                                        formSchema={ServiceScheduleSchema}
                                    />
                                </div>
                            </div>
                            <div className="form-group row">
                                <label htmlFor="make" className="col-md-5">Make *</label>
                                <div className="col-md-7">
                                    <InputField
                                        type="text"
                                        className="form-control"
                                        id="make"
                                        name="make"
                                        onChange={onChange}
                                        record={record}
                                        formSchema={ServiceScheduleSchema}
                                    />
                                </div>
                            </div>
                        </div>
                        <div className="col-lg-6 col-md-6">
                            <div className="form-group row">
                                <label htmlFor="model" className="col-md-5">Model *</label>
                                <div className="col-md-7">
                                    <InputField
                                        type="text"
                                        className="form-control"
                                        id="model"
                                        name="model"
                                        onChange={onChange}
                                        record={record}
                                        formSchema={ServiceScheduleSchema}
                                    />
                                </div>
                            </div>
                            <div className="form-group row">
                                <label htmlFor="mileage" className="col-md-5">Mileage *</label>
                                <div className="col-md-7">
                                    <InputField
                                        type="text"
                                        className="form-control"
                                        id="mileage"
                                        name="mileage"
                                        onChange={onChange}
                                        record={record}
                                        formSchema={ServiceScheduleSchema}
                                    />
                                </div>
                            </div>
                        </div>
                        <div className="col-lg-6 col-md-6">
                            <div className="form-group row align-items-center">
                                <label className="control-label col-md-5">Under Warranty? *</label>
                                <div className="col-md-7">
                                    <div
                                        style={{
                                            display: "flex",
                                            alignItems: "center",
                                            gap: "24px",
                                            flexWrap: "nowrap",
                                            marginTop: "8px",
                                        }}
                                    >
                                        <label
                                            htmlFor="warranty-yesid"
                                            style={{
                                                display: "flex",
                                                alignItems: "center",
                                                gap: "6px",
                                                margin: 0,
                                                whiteSpace: "nowrap",
                                            }}
                                        >
                                            <InputField
                                                type="radio"
                                                id="warranty-yesid"
                                                name="underWarranty"
                                                value="Yes, still under warranty"
                                                onChange={onChange}
                                                record={record}
                                                formSchema={ServiceScheduleSchema}
                                                style={{ margin: 0, position: "relative", top: "-1px" }}
                                                className={""}                                            />
        YES
                                        </label>

                                        <label
                                            htmlFor="warranty-noid"
                                            style={{
                                                display: "flex",
                                                alignItems: "center",
                                                gap: "6px",
                                                margin: 0,
                                                whiteSpace: "nowrap",
                                            }}
                                        >
                                            <InputField
                                                type="radio"
                                                id="warranty-noid"
                                                name="underWarranty"
                                                value="No"
                                                onChange={onChange}
                                                record={record}
                                                formSchema={ServiceScheduleSchema}
                                                style={{ margin: 0, position: "relative", top: "-1px" }}
                                                className={""}                                            />
        No
                                        </label>
                                    </div>
                                </div>
                            </div>
                        </div>
                    </div>
                </div>
            </div>

            <div className="row">
                <div style={{ marginTop: "10px" }} className="col-lg-12 col-md-12">
                    <div className="feature-box-5">
                        <h5>Requested Service(s)</h5>
                        <div className="col-lg-12 col-md-12">
                            <div className="form-group row">
                                <label className="col-md-12">Service(s) Needed: *</label>
                            </div>
                        </div>

                        {servicesOfferedList?.split(';')?.map((service: string, index: number) => (
                            <div key={`service-col-${index}`} className="col-lg-6 col-md-6">
                                <div className="form-group">
                                    <div className="checkbox radio-primary">
                                        <input
                                            type="checkbox"
                                            id={`service-${index}`}
                                            value={service}
                                            onChange={toggleRequestedService}
                                            checked={((record.requestedServices as string[]) || []).includes(service)}
                                        />
                                        <label htmlFor={`service-${index}`} className="ax-label-services">
                                            {service}
                                        </label>
                                    </div>
                                </div>
                            </div>
                        ))}
                        <div className="col-lg-12 col-md-12">
                            <div className="form-group">
                                <div className="checkbox radio-primary">
                                    <input
                                        type="checkbox" id="service-other" />
                                    <label htmlFor="service-other" className="ax-label-services">
                                        Other (Please Describe Below)
                                    </label>
                                </div>
                            </div>
                        </div>

                        
                        <div className="col-lg-12 col-md-12">
                            <div className="form-group row">
                                <label htmlFor="descriptionservice" className="col-md-12">Further Description / Request </label>
                                <div className="col-md-12">
                                    <TextAreaField
                                        className="form-control"
                                        id="descriptionservice"
                                        name="description"
                                        onChange={onChange}
                                        record={record}
                                        formSchema={ServiceScheduleSchema}
                                        rows={8}
                                    />
                                </div>
                            </div>
                        </div>

                        <div className="col-lg-6 col-md-6">
                            <div className="form-group row">
                                <label htmlFor="preferreddate" className="col-md-5">Preferred Date</label>
                                <div className="col-md-7">
                                    <InputField
                                        type="date"
                                        className="form-control"
                                        id="preferreddate"
                                        name="preferredDate"
                                        onChange={onChange}
                                        record={record}
                                        formSchema={ServiceScheduleSchema}
                                    />
                                </div>
                            </div>
                            <div className="form-group row">
                                <label htmlFor="career-position" className="col-md-5">Lot *</label>
                                <div className="col-md-7">
                                    <DropdownField
                                        formSchema={ServiceScheduleSchema}
                                        record={record}
                                        onChange={onChange}
                                        className="form-control"
                                        name="lotName"
                                    >
                                        <option value="">Select the Lot</option>
                                        {lotSetup.filter((c: any)=> c.emails.length > 0).map((c: any)=> (
                                            <option key={c.lotName} value={c.lotName}>{c.lotName}</option>
                                        ))}
                                    </DropdownField>
                                </div>
                            </div>
                        </div>

                        <div className="col-lg-6 col-md-6">
                            <div className="form-group row">
                                <label className="col-md-5" htmlFor="timeselector">Time *</label>
                                <div className="col-md-7">
                                    <DropdownField
                                        formSchema={ServiceScheduleSchema}
                                        record={record}
                                        onChange={onChange}
                                        className="form-control"
                                        name="time"
                                    >
                                        <option value="">Select time</option>
                                        <option value="9:00 am">7:30 am</option>
                                        <option value="9:00 am">8:00 am</option>
                                        <option value="9:00 am">8:30 am</option>
                                        <option value="9:00 am">9:00 am</option>
                                        <option value="9:30 am">9:30 am</option>
                                        <option value="10:00 am">10:00 am</option>
                                        <option value="10:30 am">10:30 am</option>
                                        <option value="11:00 am">11:00 am</option>
                                        <option value="11:30 am">11:30 am</option>
                                        <option value="12:00 pm">12:00 pm</option>
                                        <option value="12:30 pm">12:30 pm</option>
                                        <option value="1:00 pm">1:00 pm</option>
                                        <option value="1:30 pm">1:30 pm</option>
                                        <option value="2:00 pm">2:00 pm</option>
                                        <option value="2:30 pm">2:30 pm</option>
                                        <option value="3:00 pm">3:00 pm</option>
                                        <option value="3:30 pm">3:30 pm</option>
                                        <option value="4:00 pm">4:00 pm</option>
                                        <option value="4:30 pm">4:30 pm</option>
                                        <option value="5:00 pm">5:00 pm</option>
                                    </DropdownField>
                                </div>
                            </div>
                        </div>
                    </div>
                </div>
            </div>

            <div className="row">
                <div style={{ marginTop: "10px" }} className="col-lg-12 col-md-12">
                    <div className="feature-box-5">
                        <div className="form-group">
                            <label className="checkbox-inline m-l-10" htmlFor="disclaimerAcceptedId"

                                style={{ overflow: 'auto' }}
                            >
                                <InputField
                                    type="checkbox"
                                    className=""
                                    id="disclaimerAcceptedId"
                                    name="disclaimerAccepted"
                                    onChange={toggleDisclaimerAccepted}
                                    record={record}
                                    formSchema={ServiceScheduleSchema}
                                    style={{left: '40px'}}
                                />
                                <label htmlFor="disclaimerAcceptedId" style={{
                                    marginLeft: '30px',
                                    marginTop:'0px'
                                }}>
                                    <HtmlComponent htmlValue={disclaimerText} />
                                </label>
                            </label>
                        </div>

                        <div className="col-lg-12 col-md-12 col-xs-12">
                            <Captcha onValidateCaptcha={(val: boolean)=> onValidateCaptcha(val)} />
                        </div>

                        <div className="col-lg-12 col-md-12 col-xs-12">
                            {!isValid && (<div><ErrorMessage message="Please fill all the required fields!. * Mandatory Fields" /></div>)}
                            {!record.captchaVerified && (<div><ErrorMessage message="Please solve the captcha." /></div>)}
                            <button disabled={isSaving || !isValid || !record.captchaVerified} onClick={onSubmit} className="button red">
                                {isSaving ? <Loading text='Sending information...' /> : 'Send message'}
                            </button>
                            {submitError.length > 0 && (<div><ErrorMessage message={submitError} /></div>)}
                        </div>

                    </div>
                </div>
            </div>

        </div>
    );
};

export default ServiceScheduleForm;
